/**
 * §13.1 Archive — the meta layer, made visible.
 *
 * ## The problem this screen was built for
 *
 * A playtester asked for "several ships, achievements for unlock, unlocking new weapons"
 * and every one of those systems already existed in the codebase, invisible. `MetaV1`
 * has carried an `achievements` map and six `unlocks` lists since the first build,
 * `progression.ts` has gated its draw on `isUnlocked` the whole time, and the Loadout
 * screen has been printing achievement IDs in its locked-arena copy for weeks. What was
 * missing was anywhere to *see* it, and anything to *write* it (AMD-49).
 *
 * ## Three sections, in the order a player asks the questions
 *
 * 1. **Record** — "what have I done". Three lifetime figures, all of which increment
 *    today: runs, victories, longest dive. This is the only section that has ever had
 *    real data behind it, so it leads.
 * 2. **Recovered** — "how much of this game have I seen". The honest count per category
 *    against the content that exists. A first-time player reads `4 / 12 weapons` and
 *    learns the pool is three times what the Graft has been offering. This is the
 *    section that earns the screen: it is true from run 1 and it is the motivator.
 * 3. **Standing orders** — "what do I do next". Every unlock route that exists, its
 *    condition in plain language, and what it opens.
 *
 * ## Look, don't take (AMD-25)
 *
 * The Loadout carousel already settled this argument for arenas and difficulty tiers: a
 * locked entry is shown dimmed with a lock glyph and its condition, never hidden, because
 * hiding it tells a first-time player the game is a third of its real size. The Archive
 * follows it. Locked orders are listed, not filtered.
 *
 * ## Never colour alone (§13.4)
 *
 * Earned and locked differ by glyph (badge-check against lock), by a written state line,
 * and by opacity — three carriers, so the deuteranopia and tritanopia presets lose none
 * of it. The accent hue is the fourth, not the first.
 *
 * ## Read-only, on purpose
 *
 * `loadMeta()` is called on every `onShow`, and nothing here ever calls `saveMeta`.
 * `src/game.ts` holds the live `MetaV1` and is its only writer. A second writer against
 * `lumenabyss.meta.v1` would repeat the collision `core/save.ts` and `ui/settings-store.ts`
 * once had over a shared key, where both sides deleted each other's payload every boot.
 * Reading a key someone else owns is free; writing it is the bug.
 */

import { PASSIVES, WEAPONS } from '../../core/content';
import { loadMeta, type MetaV1 } from '../../core/save';
import { STARTER_POOL } from '../../sim/progression';
import { ACHIEVEMENTS, grantIds, type Achievement } from '../achievements';
import { button, div, icon, meter, meterFill, setMeter, span } from '../dom';
import { formatInt, formatTime, humanizeId } from '../format';
import { Screen, type ActionSink } from './base';

/** §10.8 arenas and §13.1.2 actives have no JSON; both tables live in `loadout.ts`. */
const ARENA_TOTAL = 3;
const ACTIVE_TOTAL = 2;
const DIFF_TOTAL = 3;

/** Everything in `STARTER_POOL` is yours on run 1, so the ledger counts it as recovered. */
function starterCount(prefix: string): number {
  let n = 0;
  for (let i = 0; i < STARTER_POOL.length; i++) {
    if (STARTER_POOL[i].startsWith(prefix)) n++;
  }
  return n;
}

interface LedgerRow {
  label: string;
  have: number;
  total: number;
  fill: HTMLElement;
  value: HTMLElement;
}

export class ArchiveScreen extends Screen {
  private runsValue: HTMLElement;
  private winsValue: HTMLElement;
  private bestValue: HTMLElement;
  private ledger: LedgerRow[] = [];
  private orderList: HTMLElement;
  private orderRows: { row: HTMLElement; mark: HTMLElement; state: HTMLElement }[] = [];

  constructor(emit: ActionSink) {
    super('archive', emit);

    const head = div('screen-head');
    head.appendChild(div('screen-title', 'ARCHIVE'));
    const back = button('Back', 'ghost', 'angle-left');
    back.addEventListener('click', () => this.emit({ type: 'goto', screen: 'title' }));
    head.appendChild(back);
    this.root.appendChild(head);

    /*
     * The premise, stated once at the top. §9.9's "unlock ≠ power" is the load-bearing
     * promise of the whole screen: a player who thinks the Archive sells stats will read
     * every locked row as "the game is stronger for someone else", which is the exact
     * feeling a run-based game cannot afford. Said here, in one line, before any number.
     */
    this.root.appendChild(
      div('archive-premise', 'Everything here changes what you begin a run with. Nothing here changes a number.'),
    );

    // --- Record ---------------------------------------------------------------
    const record = div('archive-block');
    record.appendChild(div('group-title', 'Record'));
    const stats = div('archive-record');
    this.runsValue = statCell(stats, 'runs');
    this.winsValue = statCell(stats, 'victories');
    this.bestValue = statCell(stats, 'longest dive');
    record.appendChild(stats);
    this.root.appendChild(record);

    // --- Recovered ------------------------------------------------------------
    const recovered = div('archive-block');
    recovered.appendChild(div('group-title', 'Recovered'));
    const rows = div('archive-ledger');
    this.addLedgerRow(rows, 'Weapons', WEAPONS.length);
    this.addLedgerRow(rows, 'Passives', PASSIVES.length);
    this.addLedgerRow(rows, 'Arenas', ARENA_TOTAL);
    this.addLedgerRow(rows, 'Actives', ACTIVE_TOTAL);
    this.addLedgerRow(rows, 'Descents', DIFF_TOTAL);
    recovered.appendChild(rows);
    this.root.appendChild(recovered);

    // --- Standing orders ------------------------------------------------------
    const orders = div('archive-block');
    orders.appendChild(div('group-title', 'Standing Orders'));
    this.orderList = div('archive-orders');
    for (let i = 0; i < ACHIEVEMENTS.length; i++) {
      this.orderList.appendChild(this.makeOrder(ACHIEVEMENTS[i]));
    }
    orders.appendChild(this.orderList);
    this.root.appendChild(orders);

    this.stops = [back];
  }

  private addLedgerRow(parent: HTMLElement, label: string, total: number): void {
    const row = div('ledger-row');
    row.appendChild(span('ledger-label', label));
    const value = span('ledger-value', '0 / ' + total);
    row.appendChild(value);
    // The same `.meter` every bar in the game uses, so the Archive reads as one
    // instrument with the HUD rather than as a second dialect of "progress".
    const track = meter('ledger-track');
    const fill = meterFill(track);
    row.appendChild(track);
    parent.appendChild(row);
    this.ledger.push({ label, have: 0, total, fill, value });
  }

  private makeOrder(a: Achievement): HTMLElement {
    const row = div('order-row is-locked');
    const mark = div('order-mark');
    mark.appendChild(icon('lock', 'order-glyph'));
    row.appendChild(mark);

    const body = div('order-body');
    body.appendChild(div('order-title', a.title));
    body.appendChild(div('order-label', a.label));

    // What it opens, named. "Unlocks 1 thing" is a worse sentence than the thing's name,
    // and the names are the reason to go and do it.
    const ids = grantIds(a.grants);
    if (ids.length > 0) {
      const opens = div('order-opens');
      opens.appendChild(span('order-opens-key', 'Opens'));
      for (let i = 0; i < ids.length; i++) {
        opens.appendChild(span('order-opens-name', humanizeId(ids[i])));
      }
      body.appendChild(opens);
    }

    /*
     * `blocked` is not a locked state and must not read as one. Locked says "go and do
     * this"; blocked says "the counter this needs does not exist yet", which is our
     * fault and not the player's. Saying so is better than printing a bar that cannot
     * move — a player who dashes 250 times and sees nothing happen learns the Archive
     * lies, and then believes none of it.
     */
    const state = div('order-state');
    body.appendChild(state);
    row.appendChild(body);

    this.orderRows.push({ row, mark, state });
    return row;
  }

  override onShow(): void {
    this.refresh(loadMeta());
  }

  /** Exposed for tests and for the capture harness, which has no live save file. */
  refresh(meta: MetaV1): void {
    this.runsValue.textContent = formatInt(meta.stats.runs);
    this.winsValue.textContent = formatInt(meta.stats.victories);
    this.bestValue.textContent = meta.stats.best_duration_s > 0 ? formatTime(meta.stats.best_duration_s) : '—';

    const u = meta.unlocks;
    const have = [
      starterCount('weapon.') + u.weapons.length,
      starterCount('passive.') + u.passives.length,
      1 + u.arenas.length,
      1 + u.actives.length,
      u.diff.length,
    ];
    for (let i = 0; i < this.ledger.length; i++) {
      const row = this.ledger[i];
      const n = Math.min(have[i], row.total);
      row.have = n;
      row.value.textContent = formatInt(n) + ' / ' + formatInt(row.total);
      setMeter(row.fill, row.total > 0 ? n / row.total : 0);
    }

    for (let i = 0; i < ACHIEVEMENTS.length; i++) {
      const a = ACHIEVEMENTS[i];
      const refs = this.orderRows[i];
      const earned = Object.prototype.hasOwnProperty.call(meta.achievements, a.id);
      refs.row.classList.toggle('is-locked', !earned);
      refs.row.classList.toggle('is-earned', earned);
      while (refs.mark.firstChild) refs.mark.removeChild(refs.mark.firstChild);
      refs.mark.appendChild(icon(earned ? 'badge-check' : 'lock', 'order-glyph'));
      if (earned) {
        refs.state.textContent = 'Earned';
      } else if (a.blocked === true) {
        refs.state.textContent = 'Not yet trackable';
      } else {
        refs.state.textContent = 'Locked';
      }
    }
  }

  override onBack(): boolean {
    this.emit({ type: 'goto', screen: 'title' });
    return true;
  }
}

function statCell(parent: HTMLElement, label: string): HTMLElement {
  const cell = div('archive-stat');
  const value = div('stat-value', '0');
  cell.appendChild(value);
  cell.appendChild(div('stat-label', label));
  parent.appendChild(cell);
  return value;
}
